/*
 * currentSchool — single shared accessor for the active branch.
 *
 * Returns the school object the user is currently scoped to, as
 * resolved by AuthController.attachSchoolContext on login. Used by:
 *
 *   - payslipPdf.js               · letterhead on every payslip
 *   - PaymentReceiptModal.jsx     · letterhead on every tuition receipt
 *   - StatementOfAccountPage.jsx  · letterhead on every SOA PDF
 *   - any future PDF / printable surface
 *
 * Shape returned (mirrors the public-safe payload in
 * SchoolController.publicShape):
 *   { id, code, name, shortName, logoUrl, active }
 *
 * Falls back to a sensible default if no session is present — useful
 * for build-time test harnesses and the public homepage where there
 * is no logged-in user. The fallback uses the bootstrap school's
 * historical name so existing snapshot tests and screenshots don't
 * suddenly start showing "Unknown School."
 */
const DEFAULT_SCHOOL = Object.freeze({
  id:        null,
  code:      "MAIN",
  name:      "Northpoint Center for Learning and Advancement",
  shortName: "NCLA Main",
  address:   "",
  logoUrl:   "",
  active:    true,
});

/**
 * Read the user's currently-selected school from session.
 * Returns the DEFAULT_SCHOOL fallback if no session OR if the
 * session predates Phase 2A (legacy logins with no `school` field).
 */
export function currentSchool() {
  try {
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!raw) return DEFAULT_SCHOOL;
    const u = JSON.parse(raw);
    if (u && u.school && typeof u.school === "object") {
      // Backfill defaults for any missing fields so downstream PDF
      // code can read `school.name` etc. without null-checking.
      return {
        id:        u.school.id        ?? null,
        code:      u.school.code      || DEFAULT_SCHOOL.code,
        name:      u.school.name      || DEFAULT_SCHOOL.name,
        shortName: u.school.shortName || u.school.name || DEFAULT_SCHOOL.shortName,
        address:   u.school.address   || "",
        logoUrl:   u.school.logoUrl   || "",
        active:    u.school.active !== false,
      };
    }
  } catch { /* fall through */ }
  return DEFAULT_SCHOOL;
}

/**
 * Two-line letterhead — primary name + secondary line (short name
 * or address). Used by PDF generators that have a fixed two-line
 * header band. Returns plain strings so PDF libraries that don't
 * speak unicode well don't have to deal with the school object.
 */
export function currentSchoolLetterhead() {
  const s = currentSchool();
  return {
    primary:   s.name,
    secondary: s.shortName && s.shortName !== s.name ? s.shortName : "",
    logoUrl:   s.logoUrl || "",
  };
}
